/**
 * companyDataService — sirket verisi (firma bilgisi) icin SQL-only adaptor (Faz 4 sonrasi).
 *
 * OKUMA: `getCompanyInfo` yalniz SQL (`GET /api/Company`) okur. Firebase okuma-yedegi Faz 4'te kaldirildi.
 * YAZMA: `updateCompanyInfo` / `createCompany` DAIMA dogrudan SQL'e yazar.
 */

import { mainApi, accountingApi } from '../../config/apiClient';
import { ACCOUNTING_API } from '../../config/api';

export interface CompanyBankAccount {
  bankName?: string;
  iban?: string;
}

export interface CompanyInfo {
  /** companyId (= FirebaseId). */
  id: string;
  /** Sirket adi (SQL Company.Name). Tuketiciler hem `name` hem `companyName` okuyor. */
  name: string;
  companyName: string;
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  mersisNo?: string;
  tradeRegistryNo?: string;
  logoUrl?: string;
  customDomain?: string;
  bankAccounts?: CompanyBankAccount[];
  maxPersonnel?: number;
  adminFirebaseUid?: string;
}

/** SQL GET /api/Company cevabini CompanyInfo'ya map'ler. */
function mapSqlToCompanyInfo(companyId: string, data: any): CompanyInfo {
  const name = data?.name ?? data?.companyName ?? '';
  return {
    id: data?.firebaseId ?? companyId,
    name,
    companyName: name,
    taxNumber: data?.taxNumber ?? undefined,
    taxOffice: data?.taxOffice ?? undefined,
    address: data?.address ?? undefined,
    phone: data?.phone ?? undefined,
    email: data?.email ?? undefined,
    website: data?.website ?? undefined,
    mersisNo: data?.mersisNo ?? undefined,
    tradeRegistryNo: data?.tradeRegistryNo ?? undefined,
    logoUrl: data?.logoUrl ?? undefined,
    customDomain: data?.customDomain ?? undefined,
    bankAccounts: Array.isArray(data?.bankAccounts) ? data.bankAccounts : undefined,
    maxPersonnel: data?.maxPersonnel ?? undefined,
    adminFirebaseUid: data?.adminFirebaseUid ?? undefined,
  };
}

/**
 * Firma bilgisini getirir (SADECE SQL — GET /api/Company, tenant header'ina gore).
 */
export async function getCompanyInfo(companyId: string): Promise<CompanyInfo | null> {
  const res = await mainApi.get('/api/Company');
  const info = mapSqlToCompanyInfo(companyId, res.data);
  if (!info.name) return null;
  return info;
}

/** Firma bilgisini gunceller (SADECE SQL — POST /api/Company/update). */
export async function updateCompanyInfo(_companyId: string, data: Partial<CompanyInfo>): Promise<void> {
  const payload = {
    name: data.name ?? data.companyName,
    email: data.email,
    phone: data.phone,
    address: data.address,
    taxNumber: data.taxNumber,
    taxOffice: data.taxOffice,
    website: data.website,
    mersisNo: data.mersisNo,
    tradeRegistryNo: data.tradeRegistryNo,
    logoUrl: data.logoUrl,
    customDomain: data.customDomain,
    adminFirebaseUid: data.adminFirebaseUid,
    maxPersonnel: data.maxPersonnel,
    bankAccounts: data.bankAccounts,
  };
  await mainApi.post('/api/Company/update', payload);
}

/** Yeni sirket olusturur (SqlOnly). Donus: yeni companyId. */
export async function createCompany(data: {
  name: string;
  adminFirebaseUid?: string;
  maxPersonnel?: number;
  email?: string;
  phone?: string;
  address?: string;
  taxNumber?: string;
  taxOffice?: string;
}): Promise<string> {
  const res = await mainApi.post('/api/Company/create', data);
  const companyId = res?.data?.companyId ?? res?.data?.firebaseId;
  if (!companyId) {
    throw new Error('createCompany: sunucu companyId dondurmedi');
  }
  return companyId as string;
}

// ===================== getCompanyData(path) ADAPTORLERI (Faz B) =====================

/**
 * Sirketin aktif para birimi ayarlari (SADECE SQL — GET /api/Currency/settings).
 * Eski `getCompanyData(companyId, 'currencies')` ile AYNI sekil: `{ [code]: boolean }`.
 * companyId X-Company-Id header'indan gelir.
 */
export async function getCurrencySettings(_companyId: string): Promise<Record<string, boolean>> {
  const res = await accountingApi.get(ACCOUNTING_API.CURRENCY_SETTINGS);
  const data = res?.data?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data as Record<string, boolean>;
  }
  return {};
}

/**
 * Sirketin QR menuleri — id->isim haritasi (SADECE SQL — GET /api/QRMenu/menu-definitions).
 * Eski `getCompanyData(companyId, 'qrMenus')` ile AYNI sekil: `{ [menuId]: { name } }`.
 * NOT: menu-definitions `id = firebaseId` doner (RTDB menu key ile eslesir).
 */
export async function getQrMenusMap(_companyId: string): Promise<Record<string, { name: string }>> {
  const res = await mainApi.get('/api/QRMenu/menu-definitions');
  const items = Array.isArray(res?.data?.data) ? res.data.data : [];
  const map: Record<string, { name: string }> = {};
  for (const m of items) {
    const id = String(m?.id ?? m?.firebaseId ?? '');
    if (id) map[id] = { name: m?.name ?? '' };
  }
  return map;
}
